const Trace = require('utils/app-diagnostics');

const FB = () => {

    return window.FirebasePlugin;
};

const MAX_RETRIES = 3;
const TOKEN_FETCH_COOLDOWN_MS = 10 * 60 * 1000; // 10 minutes — tune as needed

// Throttle/debounce controls for the server-flag flow
const SERVER_FLAG_COOLDOWN_MS = 1 * 60 * 1000;    // 1 minute — tune as needed
const SERVER_FLAG_DEBOUNCE_MS = 1500;             // coalesce rapid repeats

// —————————————————————————————————————————————
// Error classification
// —————————————————————————————————————————————
/**
 * @param {unknown} errStr
 * @returns {'android_play_services'|'transient_network'|'ios_apns_setup'|'bad_firebase_config'|'called_too_early'|'unknown'}
 */
const classifyTokenError = (errStr = '') => {

    const e = String(errStr || '').toLowerCase();

    if (e.includes('play services')) {
        return 'android_play_services';
    }

    if (e.includes('service_not_available') || e.includes('timeout') || e.includes('network')) {
        return 'transient_network';
    }

    if (e.includes('apns') || e.includes('apn')) {
        return 'ios_apns_setup';
    }

    if (e.includes('google app id') || e.includes('gmp') || e.includes('plist') || e.includes('google-services')) {
        return 'bad_firebase_config';
    }

    if (e.includes('not initialized') || e.includes('before')) {
        return 'called_too_early';
    }

    return 'unknown';
};

// —————————————————————————————————————————————
// Permissions helpers
// —————————————————————————————————————————————
/** @returns {Promise<boolean>} */
const ensureNotifState = () => {

    return new Promise((resolve) => {

        const plugin = FB();

        if (!plugin || !plugin.hasPermission) {
            return resolve(false);
        }

        plugin.hasPermission((granted) => resolve(Boolean(granted)));
    });
};

/** @returns {Promise<boolean>} */
const requestNotifPermission = () => {

    return new Promise((resolve) => {

        const plugin = FB();

        if (!plugin || !plugin.grantPermission) {
            return resolve(false);
        }

        plugin.grantPermission(
            () => resolve(true),
            () => resolve(false)
        );
    });
};

// —————————————————————————————————————————————
// Token fetch with retry/backoff
// —————————————————————————————————————————————
/**
 * @param {{pushNotifs?: (evt: {action:string,label:string,value?:string,meta?:string})=>void}=} analytics
 * @returns {Promise<string|null>}
 */
// eslint-disable-next-line hapi/hapi-for-you
const getFcmTokenWithRetry = async (analytics) => {

    // eslint-disable-next-line hapi/hapi-for-you
    for (let attempt = 0; attempt < MAX_RETRIES; ++attempt) {
        const plugin = FB();

        if (!plugin || !plugin.getToken) {
            return null;
        }

        const token = await new Promise((resolve) => {

            plugin.getToken(
                (t) => resolve({ ok: true, t }),
                (err) => resolve({ ok: false, err })
            );
        });

        if (token.ok) {
            analytics?.pushNotifs?.({
                action: 'getToken',
                label: 'success',
                value: attempt === 0 ? 'first_try' : `retry_${attempt}`
            });

            return token.t;
        }

        const bucket = classifyTokenError(token.err);

        analytics?.pushNotifs?.({
            action: 'getToken',
            label: 'fail',
            value: bucket,
            meta: String(token.err || '').slice(0, 200)
        });

        if (bucket !== 'transient_network' && bucket !== 'called_too_early') {
            break;
        }

        // simple backoff
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, 500 * Math.pow(2, attempt)));
    }

    return null;
};

// —————————————————————————————————————————————
// Internal state
// —————————————————————————————————————————————
let appReady = false;
let routerReady = false;
let notifGranted = false;

/** @type {Array<{data:any, raw:any, tap?:'foreground'|'background'}>} */
const pending = [];

/** @type {(evt: {data:any, raw:any, tap?:'foreground'|'background'}) => void | Promise<void> | null} */
let onPush = null;

const messageListeners = new WeakSet();
const tokenListeners = new WeakSet();

/** @type {{pushNotifs?: (evt:{action:string,label:string,value?:string,meta?:string})=>void} | null} */
let analyticsRef = null;

/** Provided by caller; we call this to persist the token */
let saveTokenFn = null;

/** Dedup / throttling */
let lastToken = null;
let lastSavedToken = null;
let lastFetchAt = 0;
let inflightTokenReq = null;
let inflightSaveReq = null;

// Server-flag throttling/debounce
let lastServerFlagHandledAt = 0;
let serverFlagTimer = null;
let inflightServerFlagReq = null;
const serverFlagPendingResolvers = [];
let serverFlagLatestForceResend = true;

// —————————————————————————————————————————————
// Delivery buffer + normalization
// —————————————————————————————————————————————
const pushDetails = ({ data, tap }) => {

    const safeId = (value) => {

        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }

        return typeof value === 'string' && /^[a-z0-9_.:-]{1,80}$/i.test(value) ? value : undefined;
    };

    return {
        tap: tap === 'foreground' || tap === 'background' ? tap : undefined,
        type: safeId(data?.type), schoolId: safeId(data?.schoolId),
        hasLink: Boolean(data?.link), hasChannelSid: Boolean(data?.channelSid)
    };
};

const attemptDelivery = (evt) => {

    const details = pushDetails(evt);
    try {
        Promise.resolve(onPush(evt)).then(
            () => Trace.record('push.delivered', details),
            () => Trace.record('push.failed', details)
        );
    }
    catch (_) {
        Trace.record('push.failed', details);
    }
};

const deliver = (evt) => {

    if (!onPush || !appReady || !routerReady) {
        pending.push(evt);
        Trace.record('push.queued', pushDetails(evt));
        return;
    }

    attemptDelivery(evt);
};

const flush = () => {

    if (!onPush || !appReady || !routerReady) {
        return;
    }

    // Ensure token is saved to DB
    if (lastToken && lastSavedToken && lastSavedToken !== lastToken) {
        saveTokenIfChanged(lastToken);
    }

    while (pending.length) {
        const evt = pending.shift();

        attemptDelivery(evt);
    }
};

/** @param {any} msg */
const normalizeMsg = (msg) => {

    const data = msg?.data && typeof msg.data === 'object' ? msg.data : msg || {};
    const tap = typeof msg?.tap === 'string' ? msg.tap : undefined; // 'foreground' | 'background' | undefined

    return { data, raw: msg, tap };
};

// —————————————————————————————————————————————
// Token save helpers (single-flight + dedupe)
// —————————————————————————————————————————————
/**
 * Save token if changed; coalesces concurrent saves.
 * @param {string} token
 * @returns {Promise<void>}
 */
const saveTokenIfChanged = (token) => {

    if (!token || typeof token !== 'string') {
        return;
    }

    if (!saveTokenFn || typeof saveTokenFn !== 'function') {
        return;
    }

    if (token === lastSavedToken && token === lastToken) {
        return;
    }

    if (inflightSaveReq) {
        return inflightSaveReq;
    }

    inflightSaveReq = (async () => {

        try {
            await Promise.resolve(saveTokenFn(token));
            lastSavedToken = token;
            analyticsRef?.pushNotifs?.({ action: 'saveToken', label: 'success' });
        }
        catch (e) {
            console.warn('[Push] saveToken failed', e);
            analyticsRef?.pushNotifs?.({
                action: 'saveToken',
                label: 'fail',
                meta: String(e && e.message ? e.message : e).slice(0, 200)
            });
        }
        finally {
            inflightSaveReq = null;
        }
    })();

    return inflightSaveReq;
};

// —————————————————————————————————————————————
// Public: fetch token (throttled) and save inside the util
// —————————————————————————————————————————————
/**
 * Try to (re)fetch the FCM token (cooldown + single-flight) and save it via saveTokenFn.
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<string|null>}
 */
const fetchTokenIfNeeded = async ({ force = false } = {}) => {

    if (inflightTokenReq) {
        return inflightTokenReq;
    }

    const now = Date.now();

    if (!force && now - lastFetchAt < TOKEN_FETCH_COOLDOWN_MS) {
        return lastToken;
    }

    inflightTokenReq = (async () => {

        try {
            const granted = await ensureNotifState();

            if (!granted) {
                // optionally surface UI elsewhere
                return null;
            }

            const token = await getFcmTokenWithRetry(analyticsRef || undefined);

            lastFetchAt = Date.now();
            lastToken = token || null;

            if (token) {
                await saveTokenIfChanged(token);
            }

            return lastToken;
        }
        finally {
            inflightTokenReq = null;
        }
    })();

    return inflightTokenReq;
};

// —————————————————————————————————————————————
// Reconcilers for "server says token missing/invalid"
// —————————————————————————————————————————————
/**
 * Refetch immediately (bypass cooldown) and save to server.
 * Optionally force-resend even if unchanged.
 *
 * @param {{ forceResend?: boolean }} [opts]  // default true
 * @returns {Promise<string|null>}
 */
const reconcileServerTokenFlag = async ({ forceResend = true } = {}) => {

    const granted = await ensureNotifState();

    if (!granted) {
        return null;
    }

    // Force a fresh fetch now
    const token = await fetchTokenIfNeeded({ force: true });

    if (!token) {
        return null;
    }

    if (typeof saveTokenFn === 'function') {
        try {
            if (forceResend) {
                await Promise.resolve(saveTokenFn(token));        // resend even if unchanged
                lastSavedToken = token;
                analyticsRef?.pushNotifs?.({
                    action: 'saveToken',
                    label: 'success',
                    value: 'server_flag'
                });
            }
            else {
                await saveTokenIfChanged(token);                  // only if changed
            }
        }
        catch (e) {
            console.warn('[Push] reconcile saveToken failed', e);
            analyticsRef?.pushNotifs?.({
                action: 'saveToken',
                label: 'fail',
                meta: String(e && e.message ? e.message : e).slice(0, 200)
            });
        }
    }

    return token;
};

/**
 * Throttled handler for the "server flag" flow.
 * - Shares a single in-flight promise across callers
 * - Enforces a cooldown between runs
 * - Returns the last known token during cooldown
 *
 * @param {{ forceResend?: boolean }} [opts]   // default true
 * @returns {Promise<string|null>}             // token or null
 */
const reconcileServerTokenFlagThrottled = (opts = {}) => {

    const { forceResend = true } = opts;

    // If a reconcile is already running, share it.
    if (inflightServerFlagReq) {
        return inflightServerFlagReq;
    }

    const now = Date.now();

    // If we're still inside the cooldown, just return the last known token.
    if (now - lastServerFlagHandledAt < SERVER_FLAG_COOLDOWN_MS) {
        return Promise.resolve(typeof lastToken === 'string' ? lastToken : null);
    }

    // Kick off a new run immediately (leading-edge throttle).
    inflightServerFlagReq = Promise.resolve()
        .then(() => reconcileServerTokenFlag({ forceResend }))
        .then((token) => {

            lastServerFlagHandledAt = Date.now();
            lastToken = token ?? null;
            return lastToken;
        })
        .finally(() => {

            inflightServerFlagReq = null;
        });

    return inflightServerFlagReq;
};

// —————————————————————————————————————————————
// Firebase wiring
// —————————————————————————————————————————————
const wireFirebase = () => {

    const plugin = FB();

    if (!plugin) {
        console.warn('[Push] FirebasePlugin not available');
        return;
    }

    // Taps must be captured before any permission or token callbacks can stall.
    if (plugin.onMessageReceived && !messageListeners.has(plugin)) {
        messageListeners.add(plugin);
        try {
            plugin.onMessageReceived(
                (msg) => {

                    const evt = normalizeMsg(msg);
                    Trace.record('push.native-received', pushDetails(evt));
                    deliver(evt);
                },
                () => Trace.record('push.failed', { stage: 'native-listener' })
            );
        }
        catch (error) {
            messageListeners.delete(plugin);
            throw error;
        }
    }

    // Refresh events → save
    if (plugin.onTokenRefresh && !tokenListeners.has(plugin)) {
        tokenListeners.add(plugin);
        try {
            plugin.onTokenRefresh(
                async (t) => {

                    lastToken = t || null;
                    lastFetchAt = Date.now();
                    await saveTokenIfChanged(t);
                },
                (err) => console.warn('[Push] onTokenRefresh error', err)
            );
        }
        catch (error) {
            tokenListeners.delete(plugin);
            throw error;
        }
    }
};

// —————————————————————————————————————————————
// Permission orchestration
// —————————————————————————————————————————————
const ensurePermission = async () => {

    notifGranted = await ensureNotifState();

    if (!notifGranted) {
        notifGranted = await requestNotifPermission();
    }

    return notifGranted;
};

// —————————————————————————————————————————————
// Public API
// —————————————————————————————————————————————
/**
 * Initialize push handling. Call once, ASAP after `deviceready`.
 *
 * @param {{
 *  onPush?: (evt:{data:any, raw:any, tap?:'foreground'|'background'})=>void,
 *  analytics?: {pushNotifs?: (evt:{action:string,label:string,value?:string,meta?:string})=>void},
 *  askPermissionNow?: boolean,
 *  saveToken?: (token:string)=>Promise<void>|void
 * }} [opts]
 */
const init = async (opts) => {

    appReady = true;
    analyticsRef = opts?.analytics || null;
    saveTokenFn = opts?.saveToken || null;

    if (typeof opts?.onPush === 'function') {
        onPush = opts.onPush;
    }

    try {
        wireFirebase();
    }
    catch (_) {
        Trace.record('push.failed', { stage: 'listener-registration' });
    }

    flush();

    try {
        if (opts?.askPermissionNow !== false) {
            await ensurePermission();
        }
    }
    catch (e) {
        console.warn('[Push] permission flow failed', e);
    }

    try {
        await fetchTokenIfNeeded({ force: false });
    }
    catch (e) {
        console.warn('[Push] initial token fetch failed', e);
    }
};

const markRouterReady = () => {

    routerReady = true;
    flush();
};

const registerHandler = (fn) => {

    onPush = typeof fn === 'function' ? fn : null;
    flush();
};

const getDiagnostics = () => {

    const plugin = FB();
    return {
        pluginAvailable: Boolean(plugin),
        initialized: appReady,
        listenerRegistered: Boolean(plugin && messageListeners.has(plugin)),
        routerReady,
        pendingCount: pending.length
    };
};

module.exports = {
    // lifecycle
    init,
    markRouterReady,
    registerHandler,
    getDiagnostics,

    // token mgmt
    fetchTokenIfNeeded, // throttled fetch + internal save
    // exposing for rare cases / tests:
    getFcmTokenWithRetry,
    saveTokenIfChanged,

    // server-flag reconcilers
    reconcileServerTokenFlag,
    reconcileServerTokenFlagThrottled,

    // helpers
    ensureNotifState,
    requestNotifPermission,
    classifyTokenError,

    // diagnostics
    _debug: {
        get pending() {

            return pending.slice();
        },
        get notifGranted() {

            return notifGranted;
        },
        get routerReady() {

            return routerReady;
        },
        get lastToken() {

            return lastToken;
        },
        get lastSavedToken() {

            return lastSavedToken;
        }
    }
};
